import { useId } from "react";

/**
 * GeneratedAvatar — the abstract geometric avatars from Judd's GFG Admin design.
 *
 * 20 configs = 10 shapes (A–J) × 2 colour variants, drawn from a fixed colour pool. Each
 * user is assigned one deterministically by id, so the same person shows the same avatar
 * on every surface (table, sidebar, impersonation bar) — matching the design's behaviour.
 *
 * Each avatar is an 80×80 SVG masked to a circle, layering: a background, a rotated rect,
 * a circle, and a line — in the four configured colours. The mask id comes from `useId()`
 * (unique per rendered instance), so many avatars — including two sharing the same seed,
 * e.g. the sidebar and a user's own blotter row — can render on one page without colliding.
 */

interface AvatarColors {
  bg: string;
  rect: string;
  circle: string;
  line: string;
}

interface AvatarConfig {
  shape: ShapeKey;
  colors: AvatarColors;
}

/** The 20 avatar configs, straight from the design's gfg-avatar-config. Typed as a
 *  non-empty tuple so index-0 is a guaranteed fallback. */
const GFG_AVATARS: [AvatarConfig, ...AvatarConfig[]] = [
  { shape: "A", colors: { bg: "#C2553A", rect: "#F2E6D0", circle: "#3A7CA5", line: "#D49B2B" } },
  { shape: "A", colors: { bg: "#9E4B5E", rect: "#F2E6D0", circle: "#5A8C6A", line: "#B87D3A" } },
  { shape: "B", colors: { bg: "#2B7A7E", rect: "#3D342F", circle: "#D06B50", line: "#F2E6D0" } },
  { shape: "B", colors: { bg: "#3A7CA5", rect: "#3D342F", circle: "#5A8C6A", line: "#D49B2B" } },
  { shape: "C", colors: { bg: "#D06B50", rect: "#F2E6D0", circle: "#2B7A7E", line: "#B87D3A" } },
  { shape: "C", colors: { bg: "#B87D3A", rect: "#F2E6D0", circle: "#C2553A", line: "#2B7A7E" } },
  { shape: "D", colors: { bg: "#D49B2B", rect: "#3D342F", circle: "#2B7A7E", line: "#F2E6D0" } },
  { shape: "D", colors: { bg: "#5A8C6A", rect: "#3D342F", circle: "#D06B50", line: "#F2E6D0" } },
  { shape: "E", colors: { bg: "#3A7CA5", rect: "#F2E6D0", circle: "#9E4B5E", line: "#C2553A" } },
  { shape: "E", colors: { bg: "#2B7A7E", rect: "#F2E6D0", circle: "#D49B2B", line: "#9E4B5E" } },
  { shape: "F", colors: { bg: "#5A8C6A", rect: "#3D342F", circle: "#B87D3A", line: "#F2E6D0" } },
  { shape: "F", colors: { bg: "#9E4B5E", rect: "#3D342F", circle: "#D49B2B", line: "#F2E6D0" } },
  { shape: "G", colors: { bg: "#F2E6D0", rect: "#5A8C6A", circle: "#2B7A7E", line: "#3D342F" } },
  { shape: "G", colors: { bg: "#F2E6D0", rect: "#D06B50", circle: "#B87D3A", line: "#3D342F" } },
  { shape: "H", colors: { bg: "#3D342F", rect: "#C2553A", circle: "#F2E6D0", line: "#5A8C6A" } },
  { shape: "H", colors: { bg: "#3D342F", rect: "#D49B2B", circle: "#F2E6D0", line: "#2B7A7E" } },
  { shape: "I", colors: { bg: "#C2553A", rect: "#F2E6D0", circle: "#3A7CA5", line: "#D49B2B" } },
  { shape: "I", colors: { bg: "#D49B2B", rect: "#F2E6D0", circle: "#9E4B5E", line: "#5A8C6A" } },
  { shape: "J", colors: { bg: "#2B7A7E", rect: "#F2E6D0", circle: "#D06B50", line: "#B87D3A" } },
  { shape: "J", colors: { bg: "#D06B50", rect: "#F2E6D0", circle: "#5A8C6A", line: "#3A7CA5" } },
];

type ShapeKey = "A" | "B" | "C" | "D" | "E" | "F" | "G" | "H" | "I" | "J";

/** Per-shape geometry: the rect box + the transforms applied to rect / circle / line. */
const SHAPES: Record<ShapeKey, { rectH: number; rectT: string; circleT: string; lineT: string }> = {
  A: {
    rectH: 80,
    rectT: "translate(-8 -8) rotate(320 40 40)",
    circleT: "translate(-3 -3)",
    lineT: "rotate(280 40 40)",
  },
  B: {
    rectH: 10,
    rectT: "translate(16 -16) rotate(336 40 40)",
    circleT: "translate(-18 18)",
    lineT: "translate(12 12) rotate(312 40 40)",
  },
  C: {
    rectH: 10,
    rectT: "translate(6 -6) rotate(130 40 40)",
    circleT: "translate(12 12)",
    lineT: "rotate(260 40 40)",
  },
  D: {
    rectH: 10,
    rectT: "translate(2 -2) rotate(176 40 40)",
    circleT: "translate(-9 9)",
    lineT: "translate(12 12) rotate(352 40 40)",
  },
  E: {
    rectH: 80,
    rectT: "translate(-6 6) rotate(240 40 40)",
    circleT: "translate(-18 -18)",
    lineT: "rotate(120 40 40)",
  },
  F: {
    rectH: 10,
    rectT: "translate(-16 -16) rotate(126 40 40)",
    circleT: "translate(-3 -3)",
    lineT: "translate(12 12) rotate(252 40 40)",
  },
  G: {
    rectH: 80,
    rectT: "translate(8 8) rotate(92 40 40)",
    circleT: "translate(18 18)",
    lineT: "translate(-4 -4) rotate(184 40 40)",
  },
  H: {
    rectH: 10,
    rectT: "translate(8 -8) rotate(338 40 40)",
    circleT: "translate(-12 -12)",
    lineT: "translate(16 16) rotate(316 40 40)",
  },
  I: {
    rectH: 80,
    rectT: "translate(10 10) rotate(310 40 40)",
    circleT: "translate(-12 -12)",
    lineT: "rotate(260 40 40)",
  },
  J: {
    rectH: 80,
    rectT: "translate(-6 6) rotate(80 40 40)",
    circleT: "translate(-3 3)",
    lineT: "rotate(160 40 40)",
  },
};

interface GeneratedAvatarProps {
  /** Stable id — the same id always yields the same avatar. */
  id: number;
  /** Rendered size in px (the SVG is a fixed 80×80 viewBox). */
  size?: number;
  alt?: string;
}

export default function GeneratedAvatar({ id, size = 28, alt }: GeneratedAvatarProps) {
  const cfg = GFG_AVATARS[Math.abs(id) % GFG_AVATARS.length] ?? GFG_AVATARS[0];
  const s = SHAPES[cfg.shape];
  const { bg, rect, circle, line } = cfg.colors;
  const maskId = `gfg-av-${useId()}`;

  return (
    <svg
      viewBox="0 0 80 80"
      width={size}
      height={size}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={alt ? `${alt} avatar` : "User avatar"}
      style={{ flexShrink: 0, display: "block" }}
    >
      <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="80" height="80">
        <rect width="80" height="80" rx="160" fill="#FFF" />
      </mask>
      <g mask={`url(#${maskId})`}>
        <rect width="80" height="80" fill={bg} />
        <rect x="10" y="30" width="80" height={s.rectH} fill={rect} transform={s.rectT} />
        <circle cx="40" cy="40" r="16" fill={circle} transform={s.circleT} />
        <line x1="0" y1="40" x2="80" y2="40" strokeWidth="2" stroke={line} transform={s.lineT} />
      </g>
    </svg>
  );
}
